// Same-origin by default; the deployed site sets VITE_API_BASE to the API host.
const BASE = import.meta.env?.VITE_API_BASE ?? ''

export class ApiError extends Error {
  constructor(message, status, options) {
    super(message, options)
    this.name = 'ApiError'
    this.status = status
  }
}

// The session cookie is set on `.trxmap.com` while the API lives on
// `api.trxmap.com`, so every account request is cross-origin. Without
// `credentials: 'include'` the browser quietly omits the cookie and a
// signed-in user looks signed out on every single call.
const CREDENTIALED = { credentials: 'include' }

async function send(path, { method = 'GET', body } = {}) {
  const init = { ...CREDENTIALED, method }
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' }
    init.body = JSON.stringify(body)
  }
  let response
  try {
    response = await fetch(`${BASE}${path}`, init)
  } catch (cause) {
    // Account and saved-place calls, not routing: naming the routing service
    // here sent people looking at the wrong thing.
    throw new ApiError('could not reach the account service', 0, { cause })
  }
  if (!response.ok) {
    let detail = `request failed with ${response.status}`
    try {
      const parsed = await response.json()
      if (parsed?.detail) detail = parsed.detail
    } catch {
      // A non-JSON error body is not worth failing over.
    }
    throw new ApiError(detail, response.status)
  }
  // 204 is the documented success for confirm/logout/delete: no body to read.
  try {
    return await response.json()
  } catch {
    return null
  }
}

async function getJson(path) {
  let response
  try {
    response = await fetch(`${BASE}${path}`)
  } catch (cause) {
    // fetch rejects with TypeError on network failure; callers should not
    // have to distinguish that from an HTTP error.
    throw new ApiError('could not reach the routing service', 0, { cause })
  }
  if (!response.ok) {
    let detail = `request failed with ${response.status}`
    try {
      const body = await response.json()
      if (body?.detail) detail = body.detail
    } catch {
      // A non-JSON error body is not worth failing over.
    }
    throw new ApiError(detail, response.status)
  }
  try {
    return await response.json()
  } catch (cause) {
    // A 200 with a non-JSON body (e.g. the SPA's index.html served by a
    // misconfigured base path or proxy) is not a successful response.
    throw new ApiError('the routing service returned an unexpected (non-JSON) response', response.status, { cause })
  }
}

/** The ranked approaches, and whether live traffic is still on its way.
 *
 * `trafficPending` is the server saying these routes were computed at free
 * flow and a snapshot is coming: it stops polling the traffic provider while
 * nobody is using the map, because every poll is a billed request, so the
 * first answer after a quiet spell arrives before the traffic does.
 */
export async function fetchRoutes({ from, to, mode }) {
  const params = new URLSearchParams({ from, to, mode })
  const body = await getJson(`/route?${params}`)
  return { routes: body.routes, trafficPending: Boolean(body.traffic_pending) }
}

export async function fetchLevels() {
  return (await getJson('/levels')).levels
}

export async function fetchDestinations() {
  return (await getJson('/destinations')).destinations
}

export async function fetchPortals() {
  return (await getJson('/portals')).portals
}

/* --- account ----------------------------------------------------------- */

/** Ask for a sign-in link. The server answers 202 whether or not the address
 *  is known, and so does this: the caller must never learn the difference. */
export async function requestLink(email) {
  await send('/auth/request', { method: 'POST', body: { email } })
}

/** Exchange a token from the emailed link for a session cookie. Only ever
 *  called from a click -- see renderCallback in auth.js. */
export async function confirmToken(token) {
  await send('/auth/confirm', { method: 'POST', body: { token } })
}

export async function logout() {
  await send('/auth/logout', { method: 'POST' })
}

/** The signed-in user, or null. Signed out is a normal answer, not an error,
 *  so a 401 comes back as null and every other failure still throws. */
export async function fetchMe() {
  try {
    return await send('/me')
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

export async function deleteAccount() {
  await send('/me', { method: 'DELETE' })
}

export async function fetchPlaces() {
  return (await send('/places'))?.places ?? []
}

export async function savePlace({ name, lat, lon }) {
  return await send('/places', { method: 'POST', body: { name, lat, lon } })
}

export async function deletePlace(id) {
  await send(`/places/${encodeURIComponent(id)}`, { method: 'DELETE' })
}
